/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { DELEGATE_ID, SESSION_ID, SESSION_KEY, USER_ID } from '../data-models/user-data';
import { StorageService } from '../services/storage.service';
import { TokenService } from '../core/auth/token.service';
import { AuthService } from '../core/auth/auth.service';
import { devAuthFlags } from '../core/auth/dev-flags';
import { PUBLIC_REQUEST, SUPPRESS_ERROR_TOAST } from '../core/api/api-context';

/**
 * Auth interceptor for the new `/auth/*` system.
 *
 * Responsibilities (auth ONLY — generic error toasting now lives in
 * `error.interceptor.ts`):
 *  - Attach `Authorization: Bearer <access>` to protected calls. The bearer is
 *    the in-memory new-auth access token, falling back to the legacy
 *    `SESSION_KEY` bearer so pre-migration sessions keep working until they
 *    next sign in.
 *  - On a 401, run the single-flight refresh (AuthService.refresh) and retry
 *    the original request ONCE. Concurrent 401s share the one refresh, so we
 *    never trigger a refresh storm (which would trip the backend's revoke-all).
 *  - On refresh failure, AuthService hard-logs-out (and shows the single
 *    "session expired" toast). We mark the request so the outer error
 *    interceptor stays silent, then propagate.
 *
 * The obsolete client-side `EXPIRY`-date logout has been removed — the server
 * 401 is the single source of truth for "your access token is no good".
 */

const AUTH_PUBLIC = ['/auth/login', '/auth/refresh'];
const EXCLUDED = ['zipcodebase'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const tokenSvc = inject(TokenService);
  const auth = inject(AuthService);

  // Public either by URL (auth endpoints) or by explicit per-request opt-in
  // (PUBLIC_REQUEST, set by callers on unauthenticated pages).
  const isAuthPublic = AUTH_PUBLIC.some((u) => req.url.includes(u)) || req.context.get(PUBLIC_REQUEST);
  const isExcluded = EXCLUDED.some((u) => req.url.includes(u));

  const userId = storage.getJsonValue(DELEGATE_ID) || storage.getJsonValue(USER_ID);
  const sessionId = storage.getJsonValue(SESSION_ID);
  const bearer = tokenSvc.getAccessToken() || storage.getJsonValue(SESSION_KEY);

  const withAuth = (token: string | null) =>
    token && !isAuthPublic && !isExcluded
      ? req.clone({
          setHeaders: { Authorization: `Bearer ${token}`, 'X-User-Id': userId, session_id: sessionId },
        })
      : req;

  const authReq = withAuth(bearer);

  const shouldAuth = !!bearer && !isAuthPublic && !isExcluded;

  // DEV-ONLY: simulate a 401 on protected requests so the refresh/retry path
  // can be tested before the backend enforces auth. No-op in production (the
  // flag is only ever set via the dev console helper).
  const base$: Observable<any> =
    devAuthFlags.force401 > 0 && shouldAuth
      ? (devAuthFlags.force401--, throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Simulated (dev)', url: req.url })))
      : next(authReq);

  return base$.pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401 && !isAuthPublic && !isExcluded) {
        // Single-flight refresh (shared across concurrent 401s), then retry once.
        return auth.refresh().pipe(
          switchMap((newAccess) => next(withAuth(newAccess))),
          catchError((refreshErr) => {
            // Refresh failed -> AuthService.forceLogout already showed the one
            // graceful "session expired" toast and redirected. Suppress the
            // outer error interceptor so a burst of queued 401s doesn't spam
            // generic error toasts, then rethrow.
            req.context.set(SUPPRESS_ERROR_TOAST, true);
            return throwError(() => refreshErr);
          }),
        );
      }
      // Non-401 errors are surfaced by the global error interceptor.
      return throwError(() => err);
    }),
  );
};
